import { Capacitor } from "@capacitor/core";
import type { Session } from "@supabase/supabase-js";
import { appendClientDebugAction } from "@/lib/debug/client-debug";
import { getNetworkErrorMessage } from "@/lib/network/errors";

const SESSION_SYNC_ENDPOINT = "/api/auth/native-session";
const SESSION_SIGNOUT_ENDPOINT = "/api/auth/signout";
const SESSION_SYNC_TIMEOUT_MS = 8_000;
const SESSION_SYNC_DEDUPE_WINDOW_MS = 60_000;
const SESSION_SYNC_CACHE_KEY = "native-session-sync";
const SESSION_SYNC_FAILED_MESSAGE =
  "We couldn't finish restoring your session. Please sign in again.";
const SESSION_SYNC_TIMEOUT_MESSAGE =
  "Session restore timed out. Please try again.";

type SessionBridgeResult = {
  ok: boolean;
  skipped?: boolean;
  error?: string;
  status?: number;
};

type CachedSessionSync = {
  key: string;
  syncedAt: number;
};

let lastSuccessfulSessionSync: CachedSessionSync | null = null;

export function resetNativeSessionBridgeCacheForTests(): void {
  lastSuccessfulSessionSync = null;
}

function serializeError(error: unknown) {
  if (error instanceof Error) {
    return {
      name: error.name,
      message: error.message,
    };
  }

  return {
    message: typeof error === "string" ? error : "unknown",
  };
}

function isAbortError(error: unknown): boolean {
  return error instanceof DOMException && error.name === "AbortError";
}

function buildSessionSyncKey(session: Session | null): string | null {
  if (!session?.access_token || !session.refresh_token) return null;
  return `${session.access_token}:${session.refresh_token}`;
}

function readCachedSessionSync(): CachedSessionSync | null {
  if (typeof window === "undefined") return lastSuccessfulSessionSync;

  if (lastSuccessfulSessionSync) return lastSuccessfulSessionSync;

  try {
    const raw = window.sessionStorage.getItem(SESSION_SYNC_CACHE_KEY);
    if (!raw) return null;

    const parsed = JSON.parse(raw) as Partial<CachedSessionSync>;
    if (typeof parsed.key !== "string" || typeof parsed.syncedAt !== "number") {
      return null;
    }

    lastSuccessfulSessionSync = {
      key: parsed.key,
      syncedAt: parsed.syncedAt,
    };
    return lastSuccessfulSessionSync;
  } catch {
    return null;
  }
}

function writeCachedSessionSync(value: CachedSessionSync): void {
  lastSuccessfulSessionSync = value;

  if (typeof window === "undefined") return;

  try {
    window.sessionStorage.setItem(SESSION_SYNC_CACHE_KEY, JSON.stringify(value));
  } catch {
    // Ignore storage failures; in-memory dedupe still helps within this session.
  }
}

function shouldSkipSessionSync(session: Session | null): boolean {
  const key = buildSessionSyncKey(session);
  if (!key) return false;

  const cached = readCachedSessionSync();
  if (!cached || cached.key !== key) return false;

  return Date.now() - cached.syncedAt < SESSION_SYNC_DEDUPE_WINDOW_MS;
}

export function isNativeSessionBridgeRequired(): boolean {
  return typeof window !== "undefined" && Capacitor.isNativePlatform();
}

async function readErrorMessage(response: Response): Promise<string | null> {
  try {
    const payload = (await response.json()) as { error?: unknown };
    return typeof payload.error === "string" && payload.error.trim().length > 0
      ? payload.error
      : null;
  } catch {
    return null;
  }
}

export async function syncNativeSessionToServer(
  session: Session | null,
  source: string
): Promise<SessionBridgeResult> {
  if (!isNativeSessionBridgeRequired()) {
    return { ok: true, skipped: true };
  }

  if (!session?.access_token || !session.refresh_token) {
    return {
      ok: false,
      error: SESSION_SYNC_FAILED_MESSAGE,
    };
  }

  if (shouldSkipSessionSync(session)) {
    appendClientDebugAction({
      source: "session-bridge",
      event: "server-session-sync-skipped",
      details: {
        source,
        reason: "recent-success",
      },
    });

    return {
      ok: true,
      skipped: true,
    };
  }

  appendClientDebugAction({
    source: "session-bridge",
    event: "server-session-sync-start",
    details: {
      source,
    },
  });

  const controller = new AbortController();
  const timeoutId = window.setTimeout(() => controller.abort(), SESSION_SYNC_TIMEOUT_MS);

  try {
    const response = await fetch(SESSION_SYNC_ENDPOINT, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      credentials: "include",
      cache: "no-store",
      body: JSON.stringify({
        accessToken: session.access_token,
        refreshToken: session.refresh_token,
        source,
      }),
      signal: controller.signal,
    });

    if (!response.ok) {
      const errorMessage =
        (await readErrorMessage(response)) ||
        (response.status === 401
          ? SESSION_SYNC_FAILED_MESSAGE
          : "We couldn't reach your account. Please try again.");

      appendClientDebugAction({
        source: "session-bridge",
        event: "server-session-sync-failed",
        details: {
          source,
          status: response.status,
          errorMessage,
        },
      });

      return {
        ok: false,
        error: errorMessage,
        status: response.status,
      };
    }

    appendClientDebugAction({
      source: "session-bridge",
      event: "server-session-sync-success",
      details: {
        source,
      },
    });

    const key = buildSessionSyncKey(session);
    if (key) {
      writeCachedSessionSync({
        key,
        syncedAt: Date.now(),
      });
    }

    return { ok: true };
  } catch (error) {
    const errorMessage = isAbortError(error)
      ? SESSION_SYNC_TIMEOUT_MESSAGE
      : getNetworkErrorMessage(error);

    appendClientDebugAction({
      source: "session-bridge",
      event: "server-session-sync-error",
      details: {
        source,
        ...serializeError(error),
      },
    });

    return {
      ok: false,
      error: errorMessage,
    };
  } finally {
    window.clearTimeout(timeoutId);
  }
}

export async function clearServerSession(source: string): Promise<void> {
  appendClientDebugAction({
    source: "session-bridge",
    event: "server-session-clear-start",
    details: {
      source,
    },
  });

  try {
    const response = await fetch(SESSION_SIGNOUT_ENDPOINT, {
      method: "POST",
      credentials: "include",
      keepalive: true,
      cache: "no-store",
    });

    appendClientDebugAction({
      source: "session-bridge",
      event: response.ok ? "server-session-clear-success" : "server-session-clear-failed",
      details: {
        source,
        status: response.status,
      },
    });
  } catch (error) {
    appendClientDebugAction({
      source: "session-bridge",
      event: "server-session-clear-error",
      details: {
        source,
        ...serializeError(error),
      },
    });
  }
}
